import { useMutation, mutationOptions, useQueryClient } from '@tanstack/react-query';

import { executeAgenticSearch } from '../../api/agenticSearchApi';
import { exportProspectQueryToCSV } from '../../api/csvExportApi';
import {
	deleteProspectQuery,
	createProspectQuery,
	updateProspectQuery,
	mergeProspectQueries,
} from '../../api/prospectQueriesApi';
import { invokeUnifiedSearch } from '../../api/unifiedSearchApi';
import { companiesKeys } from '../companies/keys';
import { jobKeys } from '../jobs/keys';

import { prospectQueryKeys, prospectQueryMutationKeys } from './keys';

export const invokeUnifiedSearchOptions = (queryClient, callbacks) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.unifiedSearch,
		mutationFn: ({ queryName, searchType, searchParameters }) =>
			invokeUnifiedSearch({
				queryName,
				searchType,
				searchParameters,
			}),
		onSuccess: async (response, variables) => {
			// Backend may force-switch HR→Maps for non-German countries
			// Use the response searchType to determine the actual query type code
			const actualSearchType = response.searchType || variables.searchType;
			const queryTypeCode = actualSearchType === 'hr' ? 'b2bheroCommercialRegisterSearch' : 'b2bheroLeadSearch';

			// Optimistic Update to add new prospect query immediately
			const queryKey = prospectQueryKeys.filtered(['b2bheroLeadSearch', 'b2bheroCommercialRegisterSearch']);
			queryClient.setQueryData(queryKey, (oldData) => {
				if (!oldData?.data) return oldData;

				const newQuery = {
					id: response.prospectQueryId,
					queryName: variables.queryName,
					createdAt: new Date().toISOString(),
					input: variables.searchParameters,
					queryTypes: {
						code: queryTypeCode,
					},
					companyCount: 0,
					apifyDatasetId: null,
					apifyRunId: null,
				};

				return {
					...oldData,
					data: [newQuery, ...oldData.data],
					totalCount: (oldData.totalCount || 0) + 1,
				};
			});

			// Invalidate job queries to refresh job status
			await queryClient.invalidateQueries({ queryKey: jobKeys.byProspectQuery });

			// Invalidate prospect queries to fetch real data from backend
			await queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });

			// Call user callback with response
			callbacks?.onSuccess?.(response);
		},
		onError: (error) => {
			callbacks?.onError?.(error);
		},
	});

export function useInvokeUnifiedSearch(callbacks) {
	const qc = useQueryClient();
	return useMutation(invokeUnifiedSearchOptions(qc, callbacks));
}

export const executeAgenticSearchOptions = (queryClient, callbacks) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.agenticSearch,
		mutationFn: ({ searchParams, queryName }) => executeAgenticSearch({ searchParams, queryName }),
		// Failures are surfaced as an assistant message inside the chat panel.
		meta: { suppressGlobalError: true },
		// No optimistic insert: the backend derives the query name from the search params
		// and does not return it, so a placeholder row would show the wrong name until refetch.
		onSuccess: async (response) => {
			await queryClient.invalidateQueries({ queryKey: jobKeys.byProspectQuery });
			await queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });

			callbacks?.onSuccess?.(response);
		},
		onError: (error) => {
			callbacks?.onError?.(error);
		},
	});

export function useExecuteAgenticSearch(callbacks) {
	const qc = useQueryClient();
	return useMutation(executeAgenticSearchOptions(qc, callbacks));
}

export const deleteProspectQueryOptions = (queryClient) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.delete,
		mutationFn: (prospectQueryId) => deleteProspectQuery(prospectQueryId),
		onMutate: async (prospectQueryId) => {
			// Cancel prospect query queries
			await queryClient.cancelQueries({ queryKey: prospectQueryKeys.all });

			const previousProspectQueries = queryClient.getQueryData(prospectQueryKeys.filtered(['b2bheroLeadSearch']));

			// Optimistically remove from prospect query caches
			queryClient.setQueriesData({ queryKey: prospectQueryKeys.all }, (old) => {
				if (!old?.data) return old;
				return {
					...old,
					data: old.data.filter((query) => query.id !== prospectQueryId),
				};
			});

			return { previousProspectQueries };
		},
		onError: (err, prospectQueryId, context) => {
			if (context?.previousProspectQueries) {
				queryClient.setQueryData(
					prospectQueryKeys.filtered(['b2bheroLeadSearch']),
					context.previousProspectQueries,
				);
			}
		},
		onSuccess: () => {
			// Invalidate all prospect query related queries
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});

export function useDeleteProspectQuery() {
	const qc = useQueryClient();
	return useMutation(deleteProspectQueryOptions(qc));
}

export const createProspectQueryOptions = (queryClient) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.create,
		mutationFn: ({ queryName, queryTypeCode, input }) => createProspectQuery(queryName, queryTypeCode, input),
		onSuccess: () => {
			// Invalidate all prospect query related queries
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});

export function useCreateProspectQuery() {
	const qc = useQueryClient();
	return useMutation(createProspectQueryOptions(qc));
}

export const updateProspectQueryOptions = (queryClient) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.update,
		mutationFn: ({ prospectQueryId, updates }) => updateProspectQuery(prospectQueryId, updates),
		onMutate: async ({ prospectQueryId, updates }) => {
			// Cancel prospect query queries
			await queryClient.cancelQueries({ queryKey: prospectQueryKeys.all });

			const previousProspectQueries = queryClient.getQueryData(prospectQueryKeys.filtered(['b2bheroLeadSearch']));

			// Optimistically update the prospect query in all caches
			queryClient.setQueriesData({ queryKey: prospectQueryKeys.all }, (old) => {
				if (!old?.data) return old;
				return {
					...old,
					data: old.data.map((query) => (query.id === prospectQueryId ? { ...query, ...updates } : query)),
				};
			});

			return { previousProspectQueries };
		},
		onError: (err, variables, context) => {
			if (context?.previousProspectQueries) {
				queryClient.setQueryData(
					prospectQueryKeys.filtered(['b2bheroLeadSearch']),
					context.previousProspectQueries,
				);
			}
		},
		onSuccess: () => {
			// Invalidate all prospect query related queries
			queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		},
	});

export function useUpdateProspectQuery() {
	const qc = useQueryClient();
	return useMutation(updateProspectQueryOptions(qc));
}

export const exportProspectQueryToCSVOptions = (callbacks) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.exportCSV,
		mutationFn: ({ prospectQueryId, options }) => exportProspectQueryToCSV(prospectQueryId, options),
		onSuccess: (data) => {
			// Call user callback with blob and filename
			callbacks?.onSuccess?.(data);
		},
		onError: (error) => {
			// Handle error and call user callback
			callbacks?.onError?.(error);
		},
	});

export function useExportProspectQueryToCSV(callbacks) {
	return useMutation(exportProspectQueryToCSVOptions(callbacks));
}

export const mergeProspectQueriesOptions = (queryClient, callbacks) =>
	mutationOptions({
		mutationKey: prospectQueryMutationKeys.merge,
		mutationFn: ({ sourceId, targetId }) => mergeProspectQueries(sourceId, targetId),
		onMutate: async ({ sourceId }) => {
			// Cancel prospect query queries
			await queryClient.cancelQueries({ queryKey: prospectQueryKeys.all });

			const previousProspectQueries = queryClient.getQueryData(
				prospectQueryKeys.filtered(['b2bheroLeadSearch', 'b2bheroCommercialRegisterSearch']),
			);

			// Optimistically remove source query from list
			// Target query companyCount will be updated after server response via invalidateQueries
			queryClient.setQueriesData({ queryKey: prospectQueryKeys.all }, (old) => {
				if (!old?.data) return old;
				return {
					...old,
					data: old.data.filter((query) => query.id !== sourceId),
				};
			});

			return { previousProspectQueries };
		},
		onError: (err, variables, context) => {
			// Rollback on error
			if (context?.previousProspectQueries) {
				queryClient.setQueryData(
					prospectQueryKeys.filtered(['b2bheroLeadSearch', 'b2bheroCommercialRegisterSearch']),
					context.previousProspectQueries,
				);
			}
			callbacks?.onError?.(err);
		},
		onSuccess: async (response, { targetId }) => {
			// Invalidate prospect queries (for companyCount in selector)
			await queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
			// Invalidate companies of target query (for updated company list in table)
			await queryClient.invalidateQueries({ queryKey: companiesKeys.byProspectQueryBase(targetId) });
			callbacks?.onSuccess?.(response);
		},
	});

export function useMergeProspectQueries(callbacks) {
	const qc = useQueryClient();
	return useMutation(mergeProspectQueriesOptions(qc, callbacks));
}
